/**
 * spin-game-cart.js
 * Zeigt im Slide-Cart-Drawer einen Hinweis auf den Mindestbestellwert,
 * wenn der gewonnene Artikel im Warenkorb liegt, der Mindestbestellwert
 * aber noch nicht erreicht wurde.
 *
 * Der Hinweis erscheint als <li class="discount-badge"> – genau dort,
 * wo der Shopify-Function-Rabatt "🎁 Lootbox Geschenk" erscheint.
 *
 * Laden: {{ 'spin-game-cart.js' | asset_url | script_tag }} in theme.liquid
 */

// =============================================================================
// Reine Logik-Funktionen (kein Browser-API, testbar in Node.js)
// =============================================================================

function findWonCartItem(items, wonVariantId) {
  if (!items || !wonVariantId) return null;
  return (
    items.find(function (item) {
      return item.properties && item.properties['_spin_win'] === '1';
    }) ||
    items.find(function (item) {
      return String(item.variant_id) === String(wonVariantId);
    }) ||
    null
  );
}

function calculateOtherTotal(items, wonCartItem) {
  if (!items || !wonCartItem) return 0;
  var cents = items
    .filter(function (item) { return item.key !== wonCartItem.key; })
    .reduce(function (sum, item) { return sum + item.line_price; }, 0);
  return cents / 100;
}

function resolveMinOrderValue(spinGameConfig, localStorageValue) {
  return (spinGameConfig && spinGameConfig.minOrderValue) ||
    parseFloat(localStorageValue) ||
    25;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { findWonCartItem, calculateOtherTotal, resolveMinOrderValue };
}

// =============================================================================
// Browser-Runtime (IIFE)
// =============================================================================
if (typeof window !== 'undefined') {
  (function () {
    'use strict';

    var LS_KEY     = 'spin_game_won_variant_id';
    var HINT_CLASS = 'spin-game-item-hint';
    var LIST_CLASS = 'spin-game-hint-list';

    // SVG-Icon identisch zum Rabatt-Badge des Themes
    var BADGE_SVG =
      '<svg aria-hidden="true" focusable="false" fill="none" width="12"' +
      ' class="icon icon-discount" viewBox="0 0 24 24">' +
      '<path d="M22.707 12.293l-11-11A1.002 1.002 0 0 0 11 1H2a1 1 0 0 0-1' +
      ' 1v9c0 .265.105.52.293.707l11 11a.997.997 0 0 0 1.414 0l9-9a.999.999' +
      ' 0 0 0 0-1.414zM7 9a2 2 0 1 1-.001-3.999A2 2 0 0 1 7 9zm6 8.414L8.586' +
      ' 13 10 11.586 14.414 16 13 17.414zm3-3L11.586 10 13 8.586 17.414 13 16' +
      ' 14.414z" fill="currentColor"></path></svg>';

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    function getWonVariantId() {
      return localStorage.getItem(LS_KEY);
    }

    async function fetchCart() {
      try {
        var res = await fetch('/cart.js');
        return await res.json();
      } catch (_) {
        return null;
      }
    }

    // -------------------------------------------------------------------------
    // <line-item>-Element des gewonnenen Artikels im Cart-Drawer finden
    // -------------------------------------------------------------------------
    function findWonItemEl(wonVariantId) {
      if (!wonVariantId) return null;
      var items = document.querySelectorAll('.cart-drawer__items line-item');
      for (var i = 0; i < items.length; i++) {
        // Primär: Link-href enthält Variant-ID
        if (items[i].querySelector('a[href*="' + wonVariantId + '"]')) {
          return items[i];
        }
        // Fallback: input data-line-key beginnt mit Variant-ID
        if (items[i].querySelector('input[data-line-key^="' + wonVariantId + ':"]')) {
          return items[i];
        }
      }
      return null;
    }

    // -------------------------------------------------------------------------
    // Hinweis als discount-badge <li> einfügen / aktualisieren
    //
    // Struktur identisch zum echten Rabatt-Badge:
    //   <ul class="v-stack ... unstyled-list" role="list">
    //     <li class="discount-badge text-xs spin-game-item-hint">
    //       [SVG] 🎁 Noch X,XX € bis zum kostenlosen Geschenk
    //     </li>
    //   </ul>
    // -------------------------------------------------------------------------
    function upsertItemHint(itemEl, text) {
      if (!itemEl || !text) return;

      var info = itemEl.querySelector('.line-item-info') || itemEl;

      // Bestehende Rabatt-Badge-Liste suchen; eigene Liste erstellen falls nötig
      var list = info.querySelector('ul[role="list"]');
      if (!list) {
        list = document.createElement('ul');
        list.className = 'v-stack justify-items-start gap-1 unstyled-list ' + LIST_CLASS;
        list.setAttribute('role', 'list');
        var qty = info.querySelector('line-item-quantity');
        if (qty) {
          qty.insertAdjacentElement('afterend', list);
        } else {
          info.appendChild(list);
        }
      }

      var hint = list.querySelector('.' + HINT_CLASS);
      if (!hint) {
        hint = document.createElement('li');
        hint.className = 'discount-badge text-xs ' + HINT_CLASS;
        hint.innerHTML = BADGE_SVG;
        list.appendChild(hint);
      }

      // Textknoten aktualisieren (SVG bleibt erhalten)
      var textNode = null;
      hint.childNodes.forEach(function (n) {
        if (n.nodeType === Node.TEXT_NODE) textNode = n;
      });
      var newText = '\u00A0' + text;   // non-breaking space als Abstand nach SVG
      if (textNode) {
        textNode.textContent = newText;
      } else {
        hint.appendChild(document.createTextNode(newText));
      }
    }

    function removeItemHint() {
      // Hints entfernen
      document.querySelectorAll('.' + HINT_CLASS).forEach(function (el) { el.remove(); });
      // Leere Listen entfernen, die wir selbst erstellt haben
      document.querySelectorAll('.' + LIST_CLASS).forEach(function (ul) {
        if (!ul.children.length) ul.remove();
      });
    }

    // -------------------------------------------------------------------------
    // Checkout-Button sperren / entsperren
    // -------------------------------------------------------------------------
    var CHECKOUT_SELECTORS = [
      '[name="checkout"]',
      '.cart__checkout-button',
      'button.cart__checkout',
      '.cart-drawer__footer [type="submit"]',
      '.drawer__footer [type="submit"]',
    ];

    function findCheckoutBtn() {
      for (var i = 0; i < CHECKOUT_SELECTORS.length; i++) {
        var el = document.querySelector(CHECKOUT_SELECTORS[i]);
        if (el) return el;
      }
      return null;
    }

    function lockCheckout(btn) {
      if (!btn || btn.dataset.spinLocked) return;
      btn.dataset.spinLocked = '1';
      btn.dataset.spinOrigDisabled = btn.disabled ? '1' : '';
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.cursor  = 'not-allowed';
      btn.title = 'Mindestbestellwert noch nicht erreicht';
    }

    function unlockCheckout(btn) {
      if (!btn || !btn.dataset.spinLocked) return;
      delete btn.dataset.spinLocked;
      btn.disabled = btn.dataset.spinOrigDisabled === '1';
      btn.style.opacity = '';
      btn.style.cursor  = '';
      btn.title = '';
    }

    // -------------------------------------------------------------------------
    // Hauptlogik
    // -------------------------------------------------------------------------
    async function evaluate() {
      var MIN_ORDER_VALUE = resolveMinOrderValue(
        window.__spinGame,
        localStorage.getItem('spin_game_min_order_value')
      );
      var wonVariantId = getWonVariantId();
      if (!wonVariantId) {
        removeItemHint();
        return;
      }

      var cart = await fetchCart();
      if (!cart || !cart.items) return;

      var wonCartItem = findWonCartItem(cart.items, wonVariantId);
      var wonItemEl   = findWonItemEl(wonVariantId);
      var btn         = findCheckoutBtn();

      if (!wonCartItem) {
        removeItemHint();
        if (btn) unlockCheckout(btn);
        return;
      }

      var otherTotal = calculateOtherTotal(cart.items, wonCartItem);

      if (otherTotal < MIN_ORDER_VALUE) {
        var remaining = MIN_ORDER_VALUE - otherTotal;
        var text = '🎁 Noch ' + remaining.toFixed(2).replace('.', ',') +
                   ' € bis zum kostenlosen Geschenk';
        if (wonItemEl) upsertItemHint(wonItemEl, text);
        if (btn) lockCheckout(btn);
      } else {
        removeItemHint();
        if (btn) unlockCheckout(btn);
      }
    }

    // -------------------------------------------------------------------------
    // Events + MutationObserver
    // -------------------------------------------------------------------------
    document.addEventListener('cart:refresh', evaluate);
    document.addEventListener('cart:updated', evaluate);
    document.addEventListener('cart:change', evaluate);

    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(evaluate, 200);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    evaluate();

  })();
}
